import * as enabledApi from '@shared/api/enabledPlayApi';
import { CommandType, GetCommandType, hotkeyPrefix } from '@shared/constants/CommandValues';
import { PROFILE_NAME_INPUT_ID } from '@shared/constants/ContentIdValues';
import { KeybindingViewModelFromModel, getDefaultKeyMapping } from '@shared/constants/Keybindings';
import { DefaultVoiceLocale } from '@shared/constants/localization/LocalizationConstants';
import { getDefaultDictationCommands, getSupportedLocale } from '@shared/hooks/helpers/LocalizationHelpers';
import { AudioEventDisplayModel } from '@shared/models/deviceProfiles/AudioEventModel';
import DeviceProfileConfiguration from '@shared/models/deviceProfiles/DeviceProfileConfiguration';
import DeviceProfileCreateUpdateModel from '@shared/models/deviceProfiles/DeviceProfileCreateUpdateModel';
import HotkeyModel from '@shared/models/deviceProfiles/HotkeyModel';
import { IEditProfileContainer } from '@shared/models/deviceProfiles/IEditProfileContainer';
import { KeyCommandDisplayMapping } from '@shared/models/deviceProfiles/KeyCommandMapping';
import { MacroDisplayCommandModel, MacroDisplayModel } from '@shared/models/deviceProfiles/MacroModel';
import UserDeviceProfileModel from '@shared/models/deviceProfiles/UserDeviceProfileModel';
import { AvailableCustomControlsModel } from '@shared/models/devices/AvailableCustomControlsModel';
import { ProfileConflictItem, ProfileConflictModel } from '@shared/models/profiles/ProfileConflictModel';
import IResult from '@shared/models/result/IResult';
import UnexpectedResult from '@shared/models/result/UnexpectedResult';
import _ from 'lodash';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createContainer, useContainer } from 'unstated-next';
import { v4 as uuidv4 } from 'uuid';
import { publish } from '../helpers/EventCallback';
import { getMacroForHotkey } from '../helpers/HotkeyHelpers';
import {
    getAggregateKeybindings,
    getConflictingKeybindingsVoiceCommandsWithDictation,
    getConflictingMacroVoiceCommandsWithDictation,
    getDuplicateAssociatedPrograms,
    getDuplicateKeybindingCommands,
    getDuplicateMacroCommands,
    getDuplicateMacroNames,
    getEmptyEvents,
    getEmptyKeybindingCommands,
    getEmptyMacroCommands,
    getUnreleasedLatchedKeys,
    keybindsFromDisplayModel,
} from '../helpers/profileUtils';
import useGlobalEditProfileConflicts from '../useGlobalEditProfileConflicts';
import useStateRef from '../useStateRef';
import ProfilesContainer from './ProfilesContainer';

function useEditProfileContainer(): IEditProfileContainer {
    const { t } = useTranslation();
    // TODO decouple this from the container
    const getGlobalEditProfileConflicts = useGlobalEditProfileConflicts();
    const profilesContainer = useContainer(ProfilesContainer);

    const [associatedPrograms, setAssociatedPrograms, associatedProgramsRef] = useStateRef<string[]>([]);
    const [audioEvents, setAudioEvents, audioEventsRef] = useStateRef<AudioEventDisplayModel[]>([]);
    const [commandKeyMappings, setCommandKeyMappings, commandKeyMappingsRef] = useStateRef<KeyCommandDisplayMapping[]>(
        []
    );
    const [description, setDescription, descriptionRef] = useStateRef<string | undefined>(undefined);
    const [dictationCommands, setDictationCommands, dictationCommandsRef] = useStateRef<string[]>([]);
    const [suggestedGeneratePrompts, setSuggestedGeneratePrompts, suggestedGeneratePromptsRef] = useStateRef<string[]>(
        []
    );
    const [suggestedRewritePrompts, setSuggestedRewritePrompts, suggestedRewritePromptsRef] = useStateRef<string[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [hasChanges, setHasChanges, hasChangesRef] = useStateRef<boolean>(false);
    const [hotkeys, setHotkeys, hotkeysRef] = useStateRef<HotkeyModel[]>([]);
    const [id, setId, idRef] = useStateRef<string | undefined>(undefined);
    const [imageUri, setImageUri, imageUriRef] = useStateRef<string | undefined>(undefined);
    const [isDefaultBindingsIncluded, setIsDefaultBindingsIncluded] = useState<boolean>(false);
    const [isDeprecatedHotkey, setIsDeprecatedHotkey, isDeprecatedHotkeyRef] = useStateRef<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isPreview, setIsPreview] = useState<boolean>(false);
    const [macros, setMacros, macrosRef] = useStateRef<MacroDisplayModel[]>([]);
    const [filter, setFilter] = useState<string>('');
    const [name, setName, nameRef] = useStateRef<string>('');
    const originalProfileRef = useRef<UserDeviceProfileModel | null>(null);
    const organizationIdRef = useRef<string | null>(null);
    const [profileConflictErrors, updateProfileConflictErrors] = useState<ProfileConflictModel[]>([]);
    const [profileConflictWarnings, updateProfileConflictWarnings] = useState<ProfileConflictModel[]>([]);
    const [defaultLocale, setDefaultLocale, defaultLocaleRef] = useStateRef<string>(DefaultVoiceLocale);
    const [originatedByProfileId, setOrignatedByProfileId] = useState<string | undefined>(undefined);
    const [isProfileConflictsAcknowledged, setIsConflictsAcknowledged] = useState<boolean>(false);
    const [availableCustomControls, setAvailableCustomControls] = useState<AvailableCustomControlsModel[]>([]);

    const saveChanges = async (): Promise<IResult<UserDeviceProfileModel>> => {
        /** Validation */
        if (!isValidProfile()) return new UnexpectedResult<UserDeviceProfileModel>();

        // Warning - Allow Save
        let conflictWarnings: ProfileConflictModel[] = [];
        detectConflictWarnings(conflictWarnings);

        // Save Profile
        const profileCreateUpdateModel = getProfileCreateUpdateModel();

        const result = idRef.current
            ? await enabledApi.updateProfile(idRef.current, profileCreateUpdateModel)
            : await enabledApi.createProfile(profileCreateUpdateModel);
        if (result?.resultType == 'Ok' && result.data) {
            setHasChanges(false);
            if (conflictWarnings.length > 0) {
                publish('alertWarning', {
                    message: t('common:profile.warning'),
                    timeout: 5000,
                });
                result.resultType = 'OkWithWarnings';
            } else {
                publish('alertSuccess', {
                    message: t('common:profile.success.save'),
                    timeout: 5000,
                });
            }
            originalProfileRef.current = _.cloneDeep(result.data);

            if (!idRef.current) {
                publish('userProfileAdded', { profile: result.data });
                setId(result.data.id);
            } else {
                publish('userProfileUpdated', { profile: result.data });
            }
        } else {
            publish('alertError', {
                message: t('common:profile.error.save'),
                timeout: 5000,
            });
        }
        return result;
    };

    const isValidProfile = (): boolean => {
        let conflictErrors: ProfileConflictModel[] = [];

        validateProfileName(conflictErrors);
        validateProfileItems(conflictErrors);
        updateProfileConflictErrors(conflictErrors);

        /** Deprecated: keeping here for consistent logic on showing an error alert  */
        const audioEventsWithEmptyCommands = audioEvents.filter((a) => a.commands.some((c) => !c.command));
        if (audioEventsWithEmptyCommands.length > 0) {
            publish('alertError', {
                message: t('common:profile.error.audioEventsWithEmptyCommands'),
                timeout: 5000,
            });
            return false;
        }

        if (conflictErrors.length > 0) {
            publish('alertError', {
                message: t('common:profile.error.conflicts'),
                timeout: 5000,
            });
        }
        return conflictErrors.length === 0;
    };

    const validateProfileName = (conflictErrors: ProfileConflictModel[]) => {
        let validName = true;
        let title = '';
        let description = '';

        if (nameRef.current == '') {
            title = t('common:profile.conflictIndicators.emptyNameTitle');
            description = t('common:profile.conflictIndicators.emptyNameDescription');
            validName = false;
        } else if (isDuplicateName()) {
            title = t('common:profile.conflictIndicators.duplicateNameTitle');
            description = t('common:profile.conflictIndicators.duplicateNameDescription');
            validName = false;
        }

        if (!validName) {
            const newConflictItem: ProfileConflictItem = {
                displayString: title,
                key: 'ProfileNameConflict',
                scrollID: PROFILE_NAME_INPUT_ID,
                pageLink: getEditProfilePath(),
            };

            addNewProfileConflictModel(
                t('common:profile.conflictIndicators.profileNameErrors'),
                [newConflictItem],
                conflictErrors,
                description
            );
        }
    };

    /** validate profile for macros, keybindings, and other item conflicts and warnings. */
    const validateProfileItems = (conflictErrors: ProfileConflictModel[]) => {
        // Macros
        addNewProfileConflictModel(
            t('common:profile.conflictIndicators.emptyControlsMapped'),
            getEmptyMacroCommands({
                macros,
                profileId: id,
                t,
            }),
            conflictErrors,
            t('common:profile.conflictIndicators.emptyControlsMappedDescription')
        );

        addNewProfileConflictModel(
            t('common:profile.conflictIndicators.duplicateInputCommandMacroTitle'),
            getDuplicateMacroCommands({
                macros,
                profileId: id,
                t,
            }),
            conflictErrors,
            t('common:profile.conflictIndicators.duplicateCommandsDescription')
        );

        addNewProfileConflictModel(
            t('common:profile.conflictIndicators.macrosReleaseKeysMissingTitle'),
            getUnreleasedLatchedKeys({
                macros,
                profileId: id,
                defaultLocale,
                t,
            }),
            conflictErrors,
            t('common:profile.conflictIndicators.macrosReleaseKeysMissingDescription')
        );

        addNewProfileConflictModel(
            t('common:profile.conflictIndicators.macrosVoiceCommandConflictWithDictationTitle'),
            getConflictingMacroVoiceCommandsWithDictation({
                macros,
                dictationCommands,
                profileId: id,
                t,
            }),
            conflictErrors,
            t('common:profile.conflictIndicators.macrosVoiceCommandConflictWithDictationDescription')
        );

        // Keybindings
        addNewProfileConflictModel(
            t('common:profile.conflictIndicators.emptyKeybindingControlsMapped'),
            getEmptyKeybindingCommands({
                commandKeyMappings,
                profileId: id,
                t,
            }),
            conflictErrors,
            t('common:profile.conflictIndicators.emptyKeybindingControlsMappedDescription')
        );

        addNewProfileConflictModel(
            t('common:profile.conflictIndicators.duplicateInputCommandBindingsTitle'),
            getDuplicateKeybindingCommands({
                commandKeyMappings,
                profileId: id,
                t,
            }),
            conflictErrors,
            t('common:profile.conflictIndicators.duplicateCommandsDescription')
        );

        addNewProfileConflictModel(
            t('common:profile.conflictIndicators.keybindingsVoiceCommandConflictWithDictationTitle'),
            getConflictingKeybindingsVoiceCommandsWithDictation({
                commandKeyMappings,
                dictationCommands,
                profileId: id,
                t,
            }),
            conflictErrors,
            t('common:profile.conflictIndicators.keybindingsVoiceCommandConflictWithDictationDescription')
        );

        // associated programs
        addNewProfileConflictModel(
            t('common:profile.conflictIndicators.duplicateAssociatedProgramNames'),
            getDuplicateAssociatedPrograms({
                associatedPrograms,
                profileId: id,
            }),
            conflictErrors,
            t('common:profile.conflictIndicators.duplicateAssociatedProgramsDescription')
        );
    };

    const detectConflictWarnings = (conflictWarnings: ProfileConflictModel[]) => {
        if (!idRef.current) return;
        const { deviceCommandConflictDisplay, globalCommandConflictDisplay } = getGlobalEditProfileConflicts(
            macros,
            dictationCommands,
            commandKeyMappings,
            idRef.current
        );
        if (globalCommandConflictDisplay?.length > 0) {
            addNewProfileConflictModel(
                t('common:customControls.conflictIndicators.globalControls'),
                globalCommandConflictDisplay,
                conflictWarnings,
                t('common:customControls.conflictIndicators.description')
            );
        }
        if (deviceCommandConflictDisplay?.length > 0) {
            addNewProfileConflictModel(
                t('common:customControls.conflictIndicators.deviceControls'),
                deviceCommandConflictDisplay,
                conflictWarnings,
                t('common:customControls.conflictIndicators.description')
            );
        }

        addNewProfileConflictModel(
            t('common:profile.conflictIndicators.duplicateMacroNames'),
            getDuplicateMacroNames({
                macros,
                profileId: id,
                t,
            }),
            conflictWarnings,
            t('common:profile.conflictIndicators.duplicateMacroNamesDescription')
        );

        addNewProfileConflictModel(
            t('common:profile.conflictIndicators.emptyEvent.title'),
            getEmptyEvents({
                macros,
                profileId: id,
                t,
            }),
            conflictWarnings,
            t('common:profile.conflictIndicators.emptyEvent.description')
        );

        // state update
        updateProfileConflictWarnings(conflictWarnings);
    };

    /** Checks whether a current profile name already exists for the user */
    const isDuplicateName = (inputName?: string): boolean => {
        const benchmarkName = inputName ?? nameRef.current;
        const duplicateNames = getDuplicateProfiles(benchmarkName);
        if (duplicateNames[0]?.id === idRef.current) {
            return false;
        } else {
            return duplicateNames.length > 0;
        }
    };

    /** ONLY to be used for public profiles, attaches a number if the profile name already exists for the user. */
    const getPublicProfileNameIfDuplicate = (inputName?: string): string => {
        const defaultName = inputName ?? name;
        const exactDuplicates = getDuplicateProfiles(defaultName);
        let updatedName;

        if (exactDuplicates.length > 0) {
            const existingDuplicates = profilesContainer.profilesRef.current.filter((profile) =>
                profile.name.startsWith(`${defaultName} #`)
            );
            if (existingDuplicates.length > 0) {
                // not doing total + 1 due to in-between profiles being deleted and avoiding more duplicates
                let lastProfileNumber = Math.max(
                    ...existingDuplicates.map((profile) => {
                        const lastChar = profile.name.at(-1);
                        return lastChar ? parseInt(lastChar) : NaN;
                    })
                );
                updatedName = `${defaultName} #${lastProfileNumber + 1}`;
            } else {
                updatedName = `${defaultName} #${1}`;
            }
        }
        return updatedName ?? defaultName;
    };

    /** Private method to get a list of all profiles with the same input name */
    const getDuplicateProfiles = (inputName: string): UserDeviceProfileModel[] => {
        const duplicateNames = profilesContainer.profilesRef.current.filter((profile) => profile.name === inputName);
        return duplicateNames;
    };

    const getUserDeviceProfileFromState = () => {
        let now = new Date();

        const profileConfig: UserDeviceProfileModel = {
            id: idRef.current,
            name: nameRef.current,
            description: descriptionRef.current,
            profileType: 'Default',
            configuration: getProfileConfigFromState(),
            createdDate: now.toISOString(),
            modifiedDate: now.toISOString(),
            defaultLocale: defaultLocaleRef.current,
            requiresEmployeeAccess: originalProfileRef.current?.requiresEmployeeAccess ?? false,
            requiresStudentAccess: originalProfileRef.current?.requiresStudentAccess ?? false,
            requiresGenericLicense: originalProfileRef.current?.requiresGenericLicense ?? false,
            requiresPlusAccess: originalProfileRef.current?.requiresPlusAccess ?? false,
            interests: originalProfileRef.current?.interests ?? [],
        };

        if (process.platform == 'darwin') {
            profileConfig.platform = 'MacOS';
        } else if (process.platform == 'win32') {
            profileConfig.platform = 'Windows';
        }

        return profileConfig;
    };

    const getProfileCreateUpdateModel = () => {
        const profileCreateUpdateModel: DeviceProfileCreateUpdateModel = {
            name: nameRef.current,
            description: description ?? '',
            profileType: 'Default',
            configuration: getProfileConfigFromState(),
            defaultLocale: defaultLocaleRef.current,
            originatedByProfileId: originatedByProfileId,
            requiresEmployeeAccess: originalProfileRef.current?.requiresEmployeeAccess ?? false,
            requiresStudentAccess: originalProfileRef.current?.requiresStudentAccess ?? false,
            requiresGenericLicense: originalProfileRef.current?.requiresGenericLicense ?? false,
            requiresPlusAccess: originalProfileRef.current?.requiresPlusAccess ?? false,
            platform: originalProfileRef.current?.platform ?? 'generic', // change later based on when we decide a user's created profiles are platform specific,
            interests: originalProfileRef.current?.interests ?? [],
            organizationId: organizationIdRef.current ?? undefined, // Associate profile with organization if organizationId is set
        };

        return profileCreateUpdateModel;
    };

    const getClonedProfile = (
        profile: UserDeviceProfileModel,
        originatedByProfileId: string | undefined,
        nameOverride?: string
    ) => {
        let clonedProfile = _.cloneDeep(profile);
        const duplicateName = getPublicProfileNameIfDuplicate(profile.name);

        // modifies and copies target properties to the cloned profile
        Object.assign(clonedProfile, {
            profileType: 'Default',
            id: undefined,
            name: nameOverride ?? duplicateName,
            originatedByProfileId,
        });

        return clonedProfile;
    };

    const getProfileConfigFromState = () => {
        const profileConfig: DeviceProfileConfiguration = {
            commandKeyMappings: keybindsFromDisplayModel(commandKeyMappingsRef.current),
            macros: macrosRef.current.map((m) => ({
                ...m,
                id: m.id ?? uuidv4(),
                commands: m.commands.map((c) => c.command),
            })),
            hotkeys: hotkeysRef.current,
            audioEvents: audioEventsRef.current.map((a) => ({ ...a, commands: a.commands.map((c) => c.command) })),
            dictationCommands: dictationCommandsRef.current,
            suggestedGeneratePrompts: suggestedGeneratePromptsRef.current,
            suggestedRewritePrompts: suggestedRewritePromptsRef.current,
            associatedPrograms: associatedProgramsRef.current,
            profileType: 'Default',
        };

        return profileConfig;
    };
    const setDefaultProfile = (locale: string) => {
        const language = getSupportedLocale(locale, 'voice') ?? DefaultVoiceLocale;
        setDefaultLocale(language);

        let keyBindings = getDefaultKeyMapping(language);
        if (!isDefaultBindingsIncluded) {
            keyBindings = keyBindings.map((k) => ({ ...k, commands: [] }));
        }
        setDescription(undefined);
        setImageUri(undefined);
        setHasChanges(false);
        setName('');
        setIsDeprecatedHotkey(false);
        setId(undefined);
        setMacros([]);
        setHotkeys([]);
        setDictationCommands(getDefaultDictationCommands(language));
        setAssociatedPrograms([]);
        setAudioEvents([]);
        setCommandKeyMappings(keyBindings);
        updateProfileConflictErrors([]);
        updateProfileConflictWarnings([]);
        setIsConflictsAcknowledged(false);
        setOrignatedByProfileId(undefined);
        organizationIdRef.current = null;
    };
    const handleLocaleChange = (value: string) => {
        setDefaultLocale(value);
        setHasChanges(true);
    };

    const updateProfileState = (profile: UserDeviceProfileModel, isInitial?: boolean) => {
        originalProfileRef.current = _.cloneDeep(profile);
        if (!profile.configuration) {
            return;
        }

        const language = getSupportedLocale(profile.defaultLocale ?? DefaultVoiceLocale, 'voice') ?? DefaultVoiceLocale;
        setDefaultLocale(language);
        const hotkeys = profile.configuration.hotkeys;
        const macros = profile.configuration.macros;
        const profileKeybindings = profile.configuration.commandKeyMappings;
        const audioEvents = profile.configuration.audioEvents;
        const dictationCommands = profile.configuration.dictationCommands;
        const suggestedGeneratePrompts = profile.configuration.suggestedGeneratePrompts;
        const suggestedRewritePrompts = profile.configuration.suggestedRewritePrompts;
        const associatedPrograms = profile.configuration.associatedPrograms ?? [];

        updateProfileConflictErrors([]);
        updateProfileConflictWarnings([]);

        macros?.forEach((m) => {
            if (!m.id) m.id = uuidv4();
        });
        const updatedMacros =
            macros?.map((a) => ({
                ...a,
                id: a.id ?? uuidv4(),
                commands: a.commands.map((c) => ({ command: c, commandType: GetCommandType(c) })),
            })) ?? [];
        setMacros(updatedMacros);
        setIsDeprecatedHotkey(false);
        hotkeys?.forEach((h) => {
            if (!h.localId) h.localId = uuidv4();
            if (!h.macroLocalId) {
                const hotkeyMacro = getMacroForHotkey(h, updatedMacros);
                h.macroLocalId = hotkeyMacro?.m?.id;
            }
            if (!h.command.startsWith(hotkeyPrefix)) {
                h.macroName = h.macroName ?? 'Deprecated Hotkey';
                setIsDeprecatedHotkey(true);
            }
        });
        setHotkeys(hotkeys ?? []);
        setAudioEvents(
            (audioEvents || []).map((a) => ({
                ...a,
                commands: (a.commands || []).map((c) => ({ command: c, commandType: GetCommandType(c) })),
            }))
        );
        setDictationCommands(dictationCommands ?? getDefaultDictationCommands(language));
        setSuggestedGeneratePrompts(suggestedGeneratePrompts ?? []);
        setSuggestedRewritePrompts(suggestedRewritePrompts ?? []);
        setAssociatedPrograms(associatedPrograms ?? []);
        setId(profile.id);
        setName(profile.name);
        setDescription(profile.description);
        setImageUri(profile.imageUrl);
        setOrignatedByProfileId(profile.originatedByProfileId);
        let keybindings = getAggregateKeybindings(
            getDefaultKeyMapping(language, profile.platform),
            profileKeybindings?.map((k) => KeybindingViewModelFromModel(k, language, [], profile.platform))
        );

        // Honor the Default Bindings flag for new profiles
        if (!isDefaultBindingsIncluded && !profile.id && !profileKeybindings) {
            keybindings = keybindings.map((k) => ({ ...k, commands: [] }));
        }

        // Set changes flag if keybindings differ or if not initial load
        // setHasChanges(keybindings.length !== profileKeybindings?.length || !isInitial);

        setCommandKeyMappings(keybindings);
        setIsConflictsAcknowledged(false);
    };

    const loadPreview = async (shareCode: string) => {
        setIsLoading(true);
        const result = await enabledApi.getProfileSharePreview(shareCode);
        if (result?.resultType == 'Ok' && result.data) {
            updateProfileState({
                ...result.data,
                name: result.data.profileName,
                id: result.data.id,
                userId: undefined,
                profileType: result.data.configuration.profileType,
                defaultLocale: result.data.defaultLocale,
                originatedByProfileId: result.data.userDeviceProfileId,
                interests: [], // gets repopulated on accepting share. If needed update api to get this value from getProfileSharePreview
            });
            setIsPreview(true);
        } else {
            publish('alertError', {
                message: t('common:profile.error.load'),
                timeout: 5000,
            });
        }
        setIsLoading(false);
        return result;
    };
    const loadPublicPreview = async (profileId: string) => {
        setIsLoading(true);
        const result = await enabledApi.getDeviceProfile(profileId);
        if (result?.resultType == 'Ok' && result.data) {
            updateProfileState({
                ...result.data,
                name: result.data.name,
                id: result.data.id,
                userId: undefined,
                profileType: result.data.configuration.profileType,
                defaultLocale: result.data.defaultLocale,
                originatedByProfileId: result.data.originatedByProfileId,
                platform: result.data.platform,
            });
            setIsPreview(true);
        } else {
            publish('alertError', {
                message: t('common:profile.error.load'),
                timeout: 5000,
            });
        }
        setIsLoading(false);
    };

    const loadOrgPreview = async (profileId: string) => {
        setIsLoading(true);
        try {
            const result = await enabledApi.getDeviceProfile(profileId ?? '');
            if (result?.resultType == 'Ok' && result.data) {
                updateProfileState({
                    ...result.data,
                    name: result.data.name,
                    id: result.data.id,
                    userId: undefined,
                    profileType: result.data.configuration.profileType,
                    defaultLocale: result.data.defaultLocale,
                    originatedByProfileId: result.data.originatedByProfileId,
                    platform: result.data.platform,
                    organizationId: result.data.organizationId,
                });
                setIsPreview(true);
            } else {
                publish('alertError', {
                    message: t('common:profile.error.load'),
                    timeout: 5000,
                });
            }
        } catch (error) {
            publish('alertError', {
                message: t('common:profile.error.load'),
                timeout: 5000,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const addPreview = async (shareCode: string) => {
        setIsLoading(true);
        const result = await enabledApi.acceptProfileShare(shareCode);
        if (result?.resultType == 'Ok' && result.data) {
            publish('userProfileAdded', { profile: result.data });
            publish('alertSuccess', {
                message: t('common:profile.success.add'),
                timeout: 5000,
            });
        } else {
            publish('alertError', {
                message: t('common:profile.error.add'),
                timeout: 5000,
            });
        }
        setIsLoading(false);
        return result;
    };

    const updateDeprecatedHotkey = (hotkey: HotkeyModel, macros: MacroDisplayModel[]) => {
        const linkedMacro = macros.find((macro) => macro.id === hotkey.macroLocalId);
        const newCommand = {
            command: hotkeyPrefix + hotkey.displayName,
            commandType: 'hotkey' as CommandType,
            hotkeyLocalId: hotkey.localId,
        };
        const hotkeys = hotkeysRef.current;
        const macroId = linkedMacro?.id ? linkedMacro.id : uuidv4();
        const updatedHotkey = { ...hotkey, command: hotkeyPrefix + hotkey.displayName, macroLocalId: macroId };
        const hotkeyIndex = hotkeys.indexOf(hotkey);
        if (linkedMacro) {
            const indexOfLinkedMacro = macros.indexOf(linkedMacro);
            const existingCommands = linkedMacro.commands;
            macros[indexOfLinkedMacro] = {
                ...linkedMacro,
                commands: [...existingCommands, newCommand],
            };
            setMacros(macros);
            hotkeys[hotkeyIndex] = updatedHotkey;
        } else {
            hotkeysRef.current.splice(hotkeyIndex, 1);
        }
        updateHotkeys([...hotkeys]);
    };

    const removeHotkeyFromMacro = (command: MacroDisplayCommandModel) => {
        const hotkey = hotkeysRef.current.find((hotkey) => hotkey.command === command.command);
        _.remove(hotkeys, (h) => h == hotkey);
        updateHotkeys([...hotkeys]);
    };

    const updateHotkeyFromMacro = (
        value: string,
        macro: MacroDisplayModel,
        index: number,
        originalValue: string
    ): string => {
        const hotkeyName = value;
        const matchHotkey = hotkeysRef.current.find((hotkey) => hotkey.command === originalValue);
        if (matchHotkey) {
            const hotkeyIndex = hotkeysRef.current.indexOf(matchHotkey);
            const updatedHotkey = {
                ...matchHotkey,
                displayName: hotkeyName.replace(hotkeyPrefix, ''),
                command: hotkeyName,
            };
            const updatedHotkeys = [...hotkeys];
            updatedHotkeys[hotkeyIndex] = updatedHotkey;
            updateHotkeys(updatedHotkeys);
            return matchHotkey.localId ?? '';
        } else {
            return addHotkeyCommandForMacro(macro, hotkeys, value);
        }
    };

    const addHotkeyCommandForMacro = (macro: MacroDisplayModel, hotkeys: HotkeyModel[], command: string): string => {
        const newHotkey = {
            command: command,
            displayName: command.replace(hotkeyPrefix, ''),
            localId: uuidv4(),
            macroId: macro.id,
            macroName: macro.name,
        };
        updateHotkeys([...hotkeys, newHotkey]);
        return newHotkey.localId;
    };

    const updateMacros = (macros: MacroDisplayModel[]) => {
        setMacros(macros);
    };

    const updateHotkeys = (hotkeys: HotkeyModel[]) => {
        setHotkeys(hotkeys);
    };

    const updateAudioEvents = (audioEvents: AudioEventDisplayModel[]) => {
        setAudioEvents(audioEvents);
    };

    const updateDictationCommands = (dictationCommands: string[]) => {
        setDictationCommands(dictationCommands);
    };

    const updateSuggestedGeneratePrompts = (prompts: string[]) => {
        setSuggestedGeneratePrompts(prompts);
    };

    const updateSuggestedRewritePrompts = (prompts: string[]) => {
        setSuggestedRewritePrompts(prompts);
    };

    const updateAssociatedPrograms = (associatedPrograms: string[]) => {
        setAssociatedPrograms(associatedPrograms);
    };

    const updateCommandKeyMappings = (keybindings: KeyCommandDisplayMapping[]) => {
        setCommandKeyMappings(keybindings);
    };

    const resetKeybindingsToDefault = () => {
        const platform = originalProfileRef.current?.platform;
        const defaultKeyBindings = getDefaultKeyMapping(defaultLocaleRef.current, platform);
        setCommandKeyMappings(defaultKeyBindings);
        setHasChanges(true);
    };

    const resetKeybindingsToEmpty = () => {
        const platform = originalProfileRef.current?.platform;
        const defaultKeyBindings = getDefaultKeyMapping(defaultLocaleRef.current, platform);
        const emptyKeyBindings = defaultKeyBindings.map((k) => ({ ...k, commands: [] }));
        setCommandKeyMappings(emptyKeyBindings);
        setHasChanges(true);
    };

    const updateName = (name: string) => {
        setName(name);
    };

    const updateDescription = (description: string) => {
        setDescription(description);
    };

    const addNewProfileConflictModel = (
        title: string,
        items: ProfileConflictItem[],
        conflictList: ProfileConflictModel[],
        subtitle?: string
    ) => {
        if (items.length > 0) {
            const newConflict: ProfileConflictModel = {
                title: title,
                items: items,
                subtitle: subtitle,
            };
            conflictList.push(newConflict);
        }
    };

    // PATH HELPERS
    const getEditProfilePath = () => {
        if (organizationIdRef.current && idRef.current) {
            console.log('Organization ID present, returning organization edit path');
            return `/org/${organizationIdRef.current}/profiles/edit/${idRef.current}`;
        }
        if (organizationIdRef.current && !idRef.current) {
            console.log('Organization ID present but no profile ID, returning organization create path');
            return ``;
        }
        return `/profiles/edit/${idRef.current}`;
    };

    const resetPreviewStatus = () => {
        setIsPreview(false);
    };

    return {
        addPreview,
        addHotkeyCommandForMacro,
        updateDeprecatedHotkey,
        associatedPrograms,
        audioEvents,
        commandKeyMappings,
        description,
        dictationCommands,
        suggestedGeneratePrompts,
        suggestedRewritePrompts,
        errors,
        filter,
        setFilter,
        getClonedProfile,
        getProfileConfigFromState,
        getPublicProfileNameIfDuplicate,
        getUserDeviceProfileFromState,
        hasChanges,
        hasChangesRef,
        hotkeys,
        id,
        idRef,
        imageUri,
        isDefaultBindingsIncluded,
        isDeprecatedHotkey,
        isDuplicateName,
        isLoading,
        isPreview,
        loadPreview,
        loadPublicPreview,
        macros,
        macrosRef,
        name,
        nameRef,
        profileConflictErrors,
        profileConflictWarnings,
        removeHotkeyFromMacro,
        saveChanges,
        setDefaultProfile,
        setHasChanges,
        setId,
        setIsDeprecatedHotkey,
        setIsDefaultBindingsIncluded,
        setIsPreview,
        updateAssociatedPrograms,
        updateAudioEvents,
        updateCommandKeyMappings,
        resetKeybindingsToDefault,
        resetKeybindingsToEmpty,
        updateDictationCommands,
        updateSuggestedGeneratePrompts,
        updateSuggestedRewritePrompts,
        updateHotkeys,
        updateHotkeyFromMacro,
        updateMacros,
        updateName,
        updateDescription,
        updateProfileState,
        defaultLocale,
        defaultLocaleRef,
        handleLocaleChange,
        isProfileConflictsAcknowledged,
        setIsConflictsAcknowledged,
        originatedByProfileId,
        originalProfileRef,
        organizationIdRef,
        resetPreviewStatus,
        availableCustomControls,
        setAvailableCustomControls,
        loadOrgPreview,
    };
}

const EditProfileContainer = createContainer(useEditProfileContainer);
export default EditProfileContainer;
